import { useRef, type ReactElement } from 'react';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@herit-fe/heritage-ui';
import { CartesianGrid, ComposedChart, Customized, ReferenceLine, usePlotArea, useXAxisTicks, XAxis, YAxis } from 'recharts';

import {
  GradientDefs,
  renderSeries,
  topRoundedKeys,
  type ResolvedValueAxis,
  type Row,
} from './chart-series';
import type { ChartSeries, ReferenceMarker, XAxisOptions } from './types';
import { useChartPointer } from './use-chart-pointer';

/**
 * 참조 마커 스택의 y 오프셋(plot 상단 기준, 위로) — 값축 단위 라벨(`viewBox.y - 14`)보다 **윗 행**에 둔다.
 * 단위 라벨이 축 바깥 모서리 정렬이라 plot 위로 뻗는데, 그 행에 마커 라벨을 두면 좌우 끝 마커(일출·일몰)와
 * 겹친다. 라벨 → 글리프 순으로 한 행씩 위로 쌓고, `MARKER_HEADROOM` 이 그만큼의 상단 여백을 보장한다.
 */
const MARKER_LABEL_DY = 28;
const MARKER_GLYPH_DY = 42;
/** 마커 스택이 요구하는 상단 여백 — 글리프 baseline(42) + ascent(13) 여유. 소비처가 `margin.top` 에 반영. */
export const MARKER_HEADROOM = 56;

/** plot 여백 — 소비처(비스크롤)·ScrollFrame(스크롤)이 축 정렬을 위해 sticky 오프셋과 **동일 값**을 넘긴다. */
export interface PlotMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface CartesianPlotProps {
  /** 막대 방향 — 세로(값축=Y) | 가로(값축=X 상단, 카테고리=Y). */
  orientation: 'vertical' | 'horizontal';
  config: ChartConfig;
  data: Row[];
  /** 가시 series(범례 토글 반영). */
  series: ChartSeries[];
  /** 해석된 값축(도메인·틱·포맷·단위) — 세로/가로 공통 적용. */
  axis: ResolvedValueAxis;
  /** 스크롤 변주: 값축을 sticky 가 그리므로 plot 값축은 숨김(그리드·도메인·틱은 공유해 정렬 불변). */
  valueAxisHidden?: boolean;
  margin: PlotMargin;
  /** 카테고리축 두께 — 가로=y(라벨)폭(`'auto'` 가능), 세로=x(라벨)높이(px). 미지정 시 recharts auto. */
  categoryAxisSize?: number | 'auto';
  /** 값축 두께(세로 막대 Y축 폭) — 보통 `CartesianChart` 가 tick 라벨 실측치를 넘긴다. 미지정 44(폴백). */
  valueAxisWidth?: number;
  /** 세로 막대 값축 상단 단위 라벨 표기(가로·스크롤 변주는 후속). */
  showUnitLabel?: boolean;
  maxBarSize?: number;
  xAxis?: XAxisOptions;
  /** 이중축(우측) — 세로 막대 전용(일부 series 가 `axis:'right'` 일 때). 좌축처럼 **해석된** 축(도메인·틱·포맷·단위). */
  rightAxis?: ResolvedValueAxis & { width?: number; hide?: boolean };
  /** 수직 참조 마커 — 세로 막대 전용 `<Customized>` 오버레이. 카테고리 tick 좌표 위 비율 위치. */
  referenceMarkers?: ReferenceMarker[];
  /** 호버 계열강조(커서 밴드 + active 카테고리 100%·나머지 dim) — 기본 true. */
  highlightOnHover?: boolean;
  /** 커스텀/기본 툴팁 콘텐츠(`false`→null 은 orchestrator 가 흡수). `ChartTooltip content` 로 전달. */
  tooltipEl: ReactElement | null;
}

/**
 * CartesianPlot — recharts `ComposedChart` 를 조립하는 **유일한 plot 렌더러**. 비스크롤·스크롤(세로/가로)
 * 모든 경로가 이 하나를 쓴다(예전 3벌 복제 제거). 방향(`orientation`)에 따라 값축/카테고리축 역할만 스왑하고,
 * 스크롤 변주는 `valueAxisHidden`(값축은 sticky 가 담당) + `margin`/`categoryAxisSize` 주입으로 흡수한다.
 * 색·툴팁·시리즈 렌더는 `ChartContainer`+`renderSeries` 공통(스크롤/비스크롤 시각 동일).
 */
export function CartesianPlot({
  orientation,
  config,
  data,
  series,
  axis,
  valueAxisHidden,
  margin,
  categoryAxisSize,
  valueAxisWidth,
  showUnitLabel,
  maxBarSize,
  xAxis,
  rightAxis,
  referenceMarkers,
  highlightOnHover = true,
  tooltipEl,
}: CartesianPlotProps) {
  const horizontal = orientation === 'horizontal';
  const hasRight = !horizontal && Boolean(rightAxis);
  const unitLabel =
    showUnitLabel && !horizontal && axis.unit ? <ValueAxisUnitLabel value={axis.unit} /> : undefined;
  const rightUnitLabel =
    showUnitLabel && !horizontal && rightAxis?.unit ? (
      <ValueAxisUnitLabel value={rightAxis.unit} side="right" />
    ) : undefined;

  // 이중축이면 series 를 좌/우로 라우팅, 단일축이면 recharts 기본 축(undefined).
  const yAxisIdOf = (s: ChartSeries): 'left' | 'right' | undefined =>
    hasRight ? (s.axis ?? 'left') : undefined;

  const categoryHeight = typeof categoryAxisSize === 'number' ? categoryAxisSize : undefined;
  // 축 tick 톤 — 값·카테고리 공통(소비처 커스텀 tick 있으면 그쪽 우선). §5 톤 통일(--axis-ink).
  const tickStyle = { fontSize: 11, fill: 'var(--axis-ink)' } as const;
  // 포인터 상호작용(계열강조 + 터치 툴팁 토글) — 각 plot 이 자체 ComposedChart 라 로컬 소유(ScrollFrame 관통 불필요).
  // 데스크탑은 recharts 호버 그대로, 터치만 탭 토글/드래그 스크럽. 상세 규칙은 `useChartPointer` 참조.
  // 컨테이너 ref — 차트 **바깥** 탭 판정(containment) 영역. 훅이 pointerdown 리스너에서만 읽는다.
  const rootRef = useRef<HTMLDivElement>(null);
  const pointer = useChartPointer(rootRef);
  const hasBar = series.some((s) => s.type === 'bar');
  // 카테고리별 상단 라운딩 대상(스택 가시 최상단) — 가시 series 기준이라 범례 토글 시 자동 재계산.
  const roundedKeys = topRoundedKeys(series, data.length);
  // 커서 — 막대=둥근 반투명 밴드, 라인 전용=대시 크로스헤어. 강조 off 면 recharts 기본.
  const cursor = highlightOnHover
    ? hasBar
      ? { fill: 'var(--axis-ink)', fillOpacity: 0.06, radius: 6 }
      : { stroke: 'var(--axis-ink)', strokeOpacity: 0.4, strokeDasharray: '4 4' }
    : undefined;

  return (
    <ChartContainer
      ref={rootRef}
      config={config}
      className="h-full w-full [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:focus-visible:outline-none [&_.recharts-surface]:outline-none"
    >
      {/* accessibilityLayer(recharts 기본 on)는 차트에 tabindex 를 달아 클릭/포커스 시 전역 focus-visible
          아웃라인(플롯 전체 amber ring)을 유발 → 끈다(마우스 호버 툴팁은 무관하게 유지). wrapper/surface
          아웃라인도 방어적으로 제거(전역 focus 규칙 대비).
          포인터 핸들러는 `highlightOnHover` 와 무관하게 **항상** 배선한다 — 터치 툴팁 토글은 계열강조와 별개
          기능이다(강조는 `dim` 플래그로 막대 쪽에서 끈다). */}
      <ComposedChart
        accessibilityLayer={false}
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={margin}
        onTouchStart={pointer.onTouchStart}
        onTouchEnd={pointer.onTouchEnd}
        barCategoryGap="6%"
      >
        <GradientDefs series={series} />
        {/* 막대 방향에 따라 값 gridline 축 반전(세로 막대=가로선, 가로 막대=세로선). 옅은 dash 로 배경에 녹임(§6). */}
        <CartesianGrid
          horizontal={!horizontal}
          vertical={horizontal}
          stroke="var(--grid)"
          strokeDasharray="3 4"
          strokeOpacity={0.5}
        />

        {horizontal ? (
          <>
            {/* 값축 = x(number, 상단), 카테고리축 = y(category, 행 라벨). */}
            <XAxis
              type="number"
              orientation="top"
              domain={axis.domain}
              ticks={axis.ticks}
              interval={0}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={tickStyle}
              hide={valueAxisHidden}
              tickFormatter={axis.tickFormatter}
            />
            <YAxis
              type="category"
              dataKey="x"
              tickLine={false}
              axisLine={false}
              width={categoryAxisSize ?? 'auto'}
              hide={xAxis?.hide}
              tick={xAxis?.tick ?? tickStyle}
              tickFormatter={xAxis?.tickFormatter}
            />
          </>
        ) : (
          <>
            {/* 카테고리축 = x(category), 값축 = y(number). */}
            <XAxis
              type="category"
              dataKey="x"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              height={categoryHeight}
              hide={xAxis?.hide}
              tick={xAxis?.tick ?? tickStyle}
              tickFormatter={xAxis?.tickFormatter}
              // 참조 마커가 있으면 전 tick 강제(interval 0) — 마커가 tick 좌표로 위치를 잡으므로 좁은 폭에서
              // recharts 가 tick 을 솎아내면(auto thinning) 마커가 사라지는 것을 방지. 마커 없으면 기본(auto).
              interval={referenceMarkers && referenceMarkers.length > 0 ? 0 : undefined}
            />
            <YAxis
              yAxisId={hasRight ? 'left' : undefined}
              type="number"
              domain={axis.domain}
              ticks={axis.ticks}
              interval={0}
              tickLine={false}
              axisLine={false}
              width={valueAxisWidth ?? 44}
              hide={valueAxisHidden}
              tick={tickStyle}
              tickFormatter={axis.tickFormatter}
              label={unitLabel}
            />
            {hasRight && (
              <YAxis
                yAxisId="right"
                orientation="right"
                type="number"
                domain={rightAxis?.domain}
                ticks={rightAxis?.ticks}
                interval={0}
                tickLine={false}
                axisLine={false}
                width={rightAxis?.width ?? 44}
                hide={rightAxis?.hide}
                tick={tickStyle}
                tickFormatter={rightAxis?.tickFormatter}
                label={rightUnitLabel}
              />
            )}
          </>
        )}

        {/* baseline(값 0선) — 그리드 나머지보다 진하게(§6·3 "바닥" 정의). 세로=y0 가로선 · 가로=x0 세로선. */}
        {horizontal ? (
          <ReferenceLine x={0} stroke="var(--axis-ink)" strokeOpacity={0.9} />
        ) : (
          <ReferenceLine
            y={0}
            yAxisId={hasRight ? 'left' : undefined}
            stroke="var(--axis-ink)"
            strokeOpacity={0.9}
          />
        )}

        {/* 툴팁은 **비제어** — 열림/닫힘 판정은 전적으로 recharts 내부 상태다. 터치 토글은 그 상태를 직접
            비우는 방식이라(`useChartPointer.closeTooltip`) 여기 `active` 를 넘길 필요가 없다. */}
        {tooltipEl && <ChartTooltip content={tooltipEl} cursor={cursor} />}

        {series.map((s) =>
          renderSeries(s, {
            yAxisId: yAxisIdOf(s),
            maxBarSize,
            rows: data,
            dim: highlightOnHover,
            horizontal,
            roundedKeys,
          }),
        )}

        {/* 수직 참조 마커 — 세로 막대(카테고리 x축) 전용. 카테고리 tick 좌표 위 비율 위치(막대 위 오버레이).
            `<Customized>` 는 child 를 chart context 내 `<Layer>` 에 마운트 → child 가 recharts v3 hook 으로
            plot 영역·tick 좌표를 읽는다(v3 는 예전처럼 xAxisMap/offset 을 prop 으로 주입하지 않음). */}
        {!horizontal && referenceMarkers && referenceMarkers.length > 0 && (
          <Customized component={<ReferenceMarkerLayer markers={referenceMarkers} />} />
        )}
      </ComposedChart>
    </ChartContainer>
  );
}

/**
 * 수직 참조 마커 레이어 — recharts **v3 hook**(`usePlotArea` plot 영역 · `useXAxisTicks` 카테고리 tick 좌표)으로
 * 각 마커의 x 를 계산해 점선 + 상단 라벨(+선택 글리프)을 그린다. tick 의 실제 렌더 **중심 좌표**(coordinate)를
 * 직접 써 밴드 anchor 모호성을 제거하고, 비율(fraction)은 인접 tick 간격(step)으로 보간: `x = tickX + step * fraction`.
 * (v3 `useXAxisScale` 은 wrapped ScaleFunction 이라 bandwidth/step 미노출 → tick 좌표를 SSOT 로.) `<Customized>`
 * 안에서 호출돼 chart store context 에 접근한다(v3 는 geometry 를 prop 아닌 hook 으로 노출).
 */
function ReferenceMarkerLayer({ markers }: { markers: ReferenceMarker[] }) {
  const plotArea = usePlotArea();
  const ticks = useXAxisTicks();
  const first = ticks?.[0];
  const last = ticks?.[ticks.length - 1];
  if (markers.length === 0 || !plotArea || !ticks || !first || !last || ticks.length < 2) return null;
  // 실제 렌더된 카테고리 tick 중심 좌표(coordinate) → 라벨→x 매핑 + 인접 간격(step, 균등 밴드).
  const coordByLabel = new Map(ticks.map((tick) => [String(tick.value), tick.coordinate] as const));
  const step = (last.coordinate - first.coordinate) / (ticks.length - 1);
  const top = plotArea.y;
  const bottom = plotArea.y + plotArea.height;
  return (
    <g>
      {markers.map((marker) => {
        const tickX = coordByLabel.get(marker.categoryLabel);
        if (tickX == null) return null;
        // tick 중심에서 분단위(fraction)만큼 다음 tick 방향으로 보간.
        const x = tickX + step * (marker.fraction ?? 0);
        const glyph = marker.glyph;
        return (
          <g key={`${marker.categoryLabel}-${marker.label}`}>
            <line
              x1={x}
              x2={x}
              y1={top}
              y2={bottom}
              stroke={marker.color}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            {glyph && (
              <text x={x} y={top - MARKER_GLYPH_DY} textAnchor="middle" fontSize={13}>
                {glyph}
              </text>
            )}
            <text
              x={x}
              y={top - MARKER_LABEL_DY}
              textAnchor="middle"
              fontSize={11}
              fill={marker.color}
              className="tabular-nums"
            >
              {marker.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/** recharts 가 YAxis `label` 로 clone 하며 주입하는 축 사각형. */
interface AxisViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 값축 상단 단위 라벨(세로 막대 Y축) — 축의 **바깥 모서리**에 붙여 plot 위 빈 여백 밴드(margin.top) 쪽으로
 * 글자가 뻗게 한다: 좌축은 축 좌변에서 시작정렬, 우축(`side='right'`)은 축 우변에서 끝정렬. 예전엔 tick 텍스트와
 * 끝정렬(축선 ∓8)이라 `(kWh/㎡)` 같은 긴 단위가 축 폭을 좌우하고(축을 라벨 실폭으로 못 줄임) 넘치면 잘렸다.
 * 단위는 **괄호로 감싸** 표기(`(개)`·`(GW)`·`(kWh/㎡)`). recharts 가 `viewBox` 주입.
 */
function ValueAxisUnitLabel({
  viewBox,
  value,
  side = 'left',
}: {
  viewBox?: AxisViewBox;
  value?: string;
  side?: 'left' | 'right';
}) {
  if (!viewBox || !value) return null;
  const isRight = side === 'right';
  return (
    <text
      x={isRight ? viewBox.x + viewBox.width : viewBox.x}
      y={viewBox.y - 14}
      textAnchor={isRight ? 'end' : 'start'}
      fill="var(--axis-ink)"
      fontSize={11}
    >
      {`(${value})`}
    </text>
  );
}
